import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  DollarSign,
  Target,
  Activity,
  Users,
  Zap,
  CreditCard,
  QrCode,
  FileText,
  AlertCircle,
} from 'lucide-react';
import { apiClient, type DashboardV2 } from '../lib/api.js';
import { CampanhasMiniTable, CriativosMiniTable } from '../components/DashboardCampaigns.js';
import { Card, CardContent } from '../components/ui/Card.js';
import { cn, formatBRL, formatPercent, formatDateTime, formatRelative } from '../lib/utils.js';

type Range = 'today' | 'yesterday' | '7d' | '30d';

function kindPill(kind: string): { label: string; cls: string } {
  if (kind === 'main') return { label: 'FRONT', cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' };
  if (kind === 'upsell') return { label: 'PRO', cls: 'bg-purple-500/15 text-purple-300 border-purple-500/30' };
  if (kind === 'order_bump') return { label: 'INSTALL', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' };
  return { label: kind.toUpperCase(), cls: 'bg-slate-500/15 text-slate-300 border-slate-500/30' };
}

function paymentIcon(method: string) {
  if (method === 'pix') return <QrCode className="h-3.5 w-3.5" />;
  if (method === 'credit_card') return <CreditCard className="h-3.5 w-3.5" />;
  return <FileText className="h-3.5 w-3.5" />;
}

function HourlyChart({ date }: { date: string | null }): JSX.Element {
  const { data } = useQuery({
    queryKey: ['dashboard-hourly', date],
    queryFn: () => apiClient.dashboardHourly(date ?? undefined),
    refetchInterval: 60_000,
  });
  if (!data) return <div className="h-56 animate-pulse bg-muted/30 rounded" />;
  const maxSpend = Math.max(...data.hours.map((h) => Number(h.spendCents)), 1);
  const totalSales = data.hours.reduce((s, h) => s + h.salesCount, 0);
  const totalSpend = data.hours.reduce((s, h) => s + Number(h.spendCents), 0);
  const totalGross = data.hours.reduce((s, h) => s + Number(h.salesGrossCents), 0);
  const peakHour = data.hours.reduce((p, h) => (Number(h.spendCents) > Number(p.spendCents) ? h : p), data.hours[0]);
  return (
    <div className="space-y-2">
      <div className="flex items-end gap-1 h-56 px-1">
        {data.hours.map((h) => {
          const sp = Number(h.spendCents);
          const heightPct = (sp / maxSpend) * 100;
          const hasSale = h.salesCount > 0;
          return (
            <div
              key={h.hour}
              className="flex-1 flex flex-col items-center justify-end gap-1 group relative"
              title={`${String(h.hour).padStart(2, '0')}h BRT · Gasto ${formatBRL(sp)} · ${h.salesCount} vendas R$ ${(Number(h.salesGrossCents) / 100).toFixed(2)}`}
            >
              {hasSale && (
                <span className="absolute -top-4 text-[9px] font-bold text-emerald-300 tabular-nums">
                  {h.salesCount}
                </span>
              )}
              <div
                className={cn(
                  'w-full rounded-sm transition-all',
                  hasSale
                    ? 'bg-gradient-to-t from-emerald-600 to-emerald-300 group-hover:brightness-125 ring-1 ring-emerald-400/40'
                    : 'bg-gradient-to-t from-cyan-600 to-cyan-300 group-hover:brightness-125',
                )}
                style={{ height: `${Math.max(heightPct, 4)}%`, minHeight: '8px' }}
              />
              <span className="text-[9px] text-muted-foreground tabular-nums">
                {String(h.hour).padStart(2, '0')}
              </span>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-4 text-[10px] text-muted-foreground px-1 pt-1 border-t border-border">
        <span>Gasto: <strong className="text-foreground">{formatBRL(totalSpend)}</strong></span>
        <span>Vendas: <strong className="text-emerald-300">{totalSales}</strong> · <strong className="text-emerald-300">{formatBRL(totalGross)}</strong></span>
        <span>Pico Meta: <strong className="text-cyan-300">{String(peakHour.hour).padStart(2, '0')}h ({formatBRL(Number(peakHour.spendCents))})</strong></span>
      </div>
    </div>
  );
}

export function Dashboard(): JSX.Element {
  const [range, setRange] = useState<Range>('today');
  const [customDate, setCustomDate] = useState<string>('');

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard-v2', range, customDate],
    queryFn: () => apiClient.dashboardV2({ range, date: customDate || undefined }),
    refetchInterval: 30_000,
  });

  if (isLoading || !data) {
    return <div className="p-8 text-muted-foreground">Carregando...</div>;
  }

  const { sales, meta, derived, productBreakdown, paymentMix, takeRate, recentSales, lastSaleAt, testsExcluded, refunds, general } = data;
  const totalPaymentCount = paymentMix.reduce((s, p) => s + p.count, 0) || 1;

  return (
    <div className="p-6 space-y-5 max-w-[1400px] mx-auto">
      {/* HEADER */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">
            NextBoot AI <span className="text-[10px] font-bold tracking-widest text-emerald-400 ml-1">OPERAÇÃO</span>
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Atualizado {formatDateTime(new Date().toISOString())} BRT · ref: {customDate || 'auto'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(['today', 'yesterday', '7d', '30d'] as const).map((r) => (
            <button
              key={r}
              onClick={() => { setRange(r); setCustomDate(''); }}
              className={cn(
                'px-3 py-1.5 rounded-md text-xs font-medium border transition-colors',
                range === r && !customDate
                  ? 'bg-cyan-500 text-slate-900 border-cyan-500'
                  : 'bg-card text-muted-foreground hover:text-foreground border-border',
              )}
            >
              {r === 'today' ? 'Hoje' : r === 'yesterday' ? 'Ontem' : r}
            </button>
          ))}
          <input
            type="date"
            value={customDate}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setCustomDate(e.target.value)}
            className="h-8 rounded-md border border-border bg-card px-2 text-xs text-foreground"
          />
        </div>
      </div>

      {/* HEALTH STRIP */}
      <div className="flex flex-wrap gap-4 px-4 py-2 rounded-md border border-border bg-card/50 text-xs">
        <div className="flex items-center gap-2">
          <span className={cn('h-2 w-2 rounded-full', lastSaleAt ? 'bg-emerald-400 animate-pulse' : 'bg-slate-500')} />
          Última venda: <strong>{lastSaleAt ? formatRelative(lastSaleAt) : '—'}</strong>
        </div>
        <div className="flex items-center gap-2 text-muted-foreground">
          <Users className="h-3 w-3" />
          {general.totalContacts} contatos · {general.hotContacts} 🔥
        </div>
        <div className="flex items-center gap-2 text-muted-foreground">
          <Activity className="h-3 w-3" />
          {general.openConversations} conversas · {general.openTickets} tickets
        </div>
        {testsExcluded.count > 0 && (
          <div className="ml-auto text-amber-300">
            <strong>{testsExcluded.count}</strong> testes excluídos ({formatBRL(testsExcluded.grossCents)})
          </div>
        )}
      </div>

      {/* KPIS GRANDES (linha do worker) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] tracking-widest uppercase text-muted-foreground">Receita real</span>
              <span className="text-[9px] font-bold bg-emerald-500/15 text-emerald-300 px-2 py-0.5 rounded-full">TICTO</span>
            </div>
            <div className="text-3xl font-bold text-emerald-400 tabular-nums">{formatBRL(sales.grossCents)}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {sales.count} vendas · ticket médio {formatBRL(sales.ticketMedioCents)}
            </div>
            <div className="text-[10px] text-muted-foreground/70 mt-1">
              Líquido após taxa: {formatBRL(sales.netCents)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] tracking-widest uppercase text-muted-foreground">Lucro estimado</span>
              <span className="text-[9px] font-bold bg-emerald-500/15 text-emerald-300 px-2 py-0.5 rounded-full">LUCRO</span>
            </div>
            <div className={cn('text-3xl font-bold tabular-nums', derived.lucroEstimadoCents >= 0 ? 'text-emerald-400' : 'text-rose-400')}>
              {formatBRL(derived.lucroEstimadoCents)}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Margem {formatPercent(derived.margem)} · receita líquida − ad spend
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] tracking-widest uppercase text-muted-foreground">Gasto Meta Ads</span>
              <span className="text-[9px] font-bold bg-blue-500/15 text-blue-300 px-2 py-0.5 rounded-full">META</span>
            </div>
            <div className="text-3xl font-bold text-blue-400 tabular-nums">{formatBRL(meta.spendCents)}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {meta.impressions.toLocaleString('pt-BR')} imp · CPM {formatBRL(meta.cpm)} · CTR {formatPercent(meta.ctr)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] tracking-widest uppercase text-muted-foreground">ROAS real</span>
              <span className={cn('text-[9px] font-bold px-2 py-0.5 rounded-full', derived.roas >= 2 ? 'bg-emerald-500/15 text-emerald-300' : derived.roas >= 1 ? 'bg-amber-500/15 text-amber-300' : 'bg-rose-500/15 text-rose-300')}>
                {derived.roas >= 2 ? '✓ BOM' : derived.roas >= 1 ? 'OK' : '⚠ BAIXO'}
              </span>
            </div>
            <div className={cn('text-3xl font-bold tabular-nums', derived.roas >= 1 ? 'text-emerald-400' : 'text-rose-400')}>
              {derived.roas.toFixed(2)}x
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Ticto bruto / gasto · CPA {formatBRL(derived.cpaCents)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* CARDS DETALHE 4 colunas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Breakdown produto */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] tracking-widest uppercase text-muted-foreground">Vendas por produto</span>
            </div>
            <div className="space-y-2">
              {productBreakdown.map((p) => {
                const k = kindPill(p.productKind);
                return (
                  <div key={p.productKind} className="flex items-center justify-between text-sm">
                    <span className={cn('px-2 py-0.5 rounded text-[10px] font-bold border', k.cls)}>{k.label}</span>
                    <span className="tabular-nums">
                      {p.count} · <strong>{formatBRL(p.grossCents)}</strong>
                    </span>
                  </div>
                );
              })}
              {productBreakdown.length === 0 && (
                <div className="text-xs text-muted-foreground">Sem vendas no período.</div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Take rate */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] tracking-widest uppercase text-muted-foreground">Aceite upsell / bump</span>
              <span className="text-[9px] font-bold bg-blue-500/15 text-blue-300 px-2 py-0.5 rounded-full">TAKE RATE</span>
            </div>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Upsell PRO</span>
                  <span className="font-bold tabular-nums">{formatPercent(takeRate.upsellRate)}</span>
                </div>
                <div className="h-1.5 rounded-full bg-muted/40 overflow-hidden">
                  <div className="h-full bg-purple-400" style={{ width: `${Math.min(takeRate.upsellRate * 100, 100)}%` }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Order bump install</span>
                  <span className="font-bold tabular-nums">{formatPercent(takeRate.bumpRate)}</span>
                </div>
                <div className="h-1.5 rounded-full bg-muted/40 overflow-hidden">
                  <div className="h-full bg-amber-400" style={{ width: `${Math.min(takeRate.bumpRate * 100, 100)}%` }} />
                </div>
              </div>
              <div className="text-[10px] text-muted-foreground pt-1 border-t border-border">
                Base: {takeRate.fronts} fronts · {takeRate.upsells} upsells · {takeRate.bumps} bumps
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Mix pgto */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] tracking-widest uppercase text-muted-foreground">Forma de pagamento</span>
              <span className="text-[9px] font-bold bg-slate-500/15 text-slate-300 px-2 py-0.5 rounded-full">MIX</span>
            </div>
            <div className="space-y-2">
              {paymentMix.map((p) => (
                <div key={p.paymentMethod} className="flex items-center gap-2 text-sm">
                  <span className="flex items-center gap-1.5 text-muted-foreground w-24">
                    {paymentIcon(p.paymentMethod)}
                    {p.paymentMethod}
                  </span>
                  <div className="flex-1 h-1.5 rounded-full bg-muted/40 overflow-hidden">
                    <div
                      className={cn('h-full', p.paymentMethod === 'pix' ? 'bg-cyan-400' : p.paymentMethod === 'credit_card' ? 'bg-emerald-400' : 'bg-amber-400')}
                      style={{ width: `${(p.count / totalPaymentCount) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs font-bold tabular-nums w-8 text-right">{p.count}</span>
                </div>
              ))}
              {paymentMix.length === 0 && (
                <div className="text-xs text-muted-foreground">Sem dados.</div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Refunds / health */}
        <Card className="border-amber-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] tracking-widest uppercase text-muted-foreground">Saúde 30d</span>
              <span
                className="text-[9px] font-bold bg-amber-500/15 text-amber-300 px-2 py-0.5 rounded-full cursor-help"
                title="Workflow manual ativo desde Sprint 17: marque refunds em /vendas/:id. Ledger e Saúde 30d atualizam sozinhos. Worker v2.0 ainda não escuta webhook 'refunded' — pendência ADR pra ajustar."
              >
                ⓘ MANUAL
              </span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Reembolsos</span>
                <span className="font-bold tabular-nums text-muted-foreground line-through decoration-amber-500/50">
                  {refunds.refunds30d}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Chargebacks</span>
                <span className="font-bold tabular-nums text-muted-foreground line-through decoration-amber-500/50">
                  {refunds.chargebacks30d}
                </span>
              </div>
              <div className="text-[9px] text-amber-300 leading-tight pt-1.5 border-t border-amber-500/20 bg-amber-500/5 -mx-2 px-2 py-1.5 rounded">
                <strong>Workflow manual ativo.</strong> Quando aparecer refund/chargeback na Ticto,
                abra <Link to="/vendas" className="underline hover:text-amber-200">/vendas</Link>,
                clique na venda e use o botão âmbar <em>Marcar reembolsado</em> — o ledger ajusta
                sozinho. Worker v2.0 não escuta status <code className="bg-amber-500/10 px-1 rounded">refunded</code> ainda.
              </div>
              <div className="flex justify-between pt-2 border-t border-border">
                <span className="text-muted-foreground text-xs">Leads ativos</span>
                <span className="text-xs font-bold tabular-nums">{general.activeLeads}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground text-xs">Conversas urgentes</span>
                <span className="text-xs font-bold tabular-nums">{general.urgentConversations}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* CURVA HORÁRIA (só pra today/yesterday/customDate) */}
      {(range === 'today' || range === 'yesterday' || customDate) && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] tracking-widest uppercase text-muted-foreground">Curva horária — gasto + vendas (verde = vendeu)</span>
              <span className="text-[10px] text-muted-foreground">BRT</span>
            </div>
            <HourlyChart
              date={
                customDate ? customDate
                : range === 'yesterday' ? new Date(Date.now() - 86400000).toISOString().slice(0, 10)
                : null
              }
            />
          </CardContent>
        </Card>
      )}

      {/* TABELA DE VENDAS */}
      <Card>
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <h2 className="text-sm font-bold">
              Vendas {sales.count > 0 && <span className="text-muted-foreground">{sales.count} / {formatBRL(sales.grossCents)}</span>}
            </h2>
            {testsExcluded.count > 0 && (
              <span className="text-[10px] text-muted-foreground">
                testes excluídos: {testsExcluded.count} / {formatBRL(testsExcluded.grossCents)}
              </span>
            )}
          </div>
          {recentSales.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground">
              Sem vendas no período selecionado.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/30 border-b border-border">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Cliente</th>
                    <th className="px-4 py-2 text-left font-medium">Tipo</th>
                    <th className="px-4 py-2 text-right font-medium">Valor</th>
                    <th className="px-4 py-2 text-left font-medium">Pagto</th>
                    <th className="px-4 py-2 text-left font-medium">Hora BRT</th>
                    <th className="px-4 py-2 text-left font-medium">Email</th>
                    <th className="px-4 py-2 text-left font-medium">UTM</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/40">
                  {recentSales.map((o) => {
                    const k = kindPill(o.productKind);
                    return (
                      <tr key={o.id} className="hover:bg-muted/20">
                        <td className="px-4 py-2">{o.contactName ?? '—'}</td>
                        <td className="px-4 py-2">
                          <span className={cn('px-2 py-0.5 rounded text-[9px] font-bold border', k.cls)}>{k.label}</span>
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          <div className="font-bold">{formatBRL(o.amountGrossCents)}</div>
                          <div className="text-[10px] text-muted-foreground">líq {formatBRL(o.netCents)}</div>
                        </td>
                        <td className="px-4 py-2">
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                            {paymentIcon(o.paymentMethod)}
                            {o.paymentMethod}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-xs text-muted-foreground tabular-nums">
                          {formatDateTime(o.createdAt)}
                        </td>
                        <td className="px-4 py-2 text-xs text-muted-foreground truncate max-w-[180px]">
                          {o.contactEmail ?? '—'}
                        </td>
                        <td className="px-4 py-2 text-xs text-muted-foreground">
                          {o.utmSource ? (
                            <span className="text-cyan-400">
                              {o.utmSource}
                              {o.utmCampaign ? ` · ${o.utmCampaign}` : ''}
                            </span>
                          ) : (
                            <span>direto</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Campanhas — o que Lucas mais acompanha */}
      <Card>
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <h2 className="text-sm font-bold flex items-center gap-2">
              <Target className="h-4 w-4 text-blue-400" />
              Campanhas — gasto e performance
            </h2>
            <Link to="/trafego" className="text-[10px] text-cyan-300 hover:text-cyan-200">ver todas →</Link>
          </div>
          <CampanhasMiniTable />
        </CardContent>
      </Card>

      {/* Top criativos */}
      <Card>
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <h2 className="text-sm font-bold flex items-center gap-2">
              <Zap className="h-4 w-4 text-amber-400" />
              Top criativos (ad-level)
            </h2>
            <span className="text-[10px] text-muted-foreground">top 15 por gasto</span>
          </div>
          <CriativosMiniTable />
        </CardContent>
      </Card>

      {/* Quick links */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
        <Link to="/vendas" className="block p-3 rounded-lg border border-border bg-card hover:bg-muted/30 transition">
          <DollarSign className="h-4 w-4 text-emerald-400" />
          <div className="mt-2 font-medium text-sm">Vendas</div>
          <div className="text-[10px] text-muted-foreground">Drill venda real</div>
        </Link>
        <Link to="/trafego" className="block p-3 rounded-lg border border-border bg-card hover:bg-muted/30 transition">
          <Target className="h-4 w-4 text-blue-400" />
          <div className="mt-2 font-medium text-sm">Tráfego</div>
          <div className="text-[10px] text-muted-foreground">Meta campanhas + ROAS</div>
        </Link>
        <Link to="/financeiro" className="block p-3 rounded-lg border border-border bg-card hover:bg-muted/30 transition">
          <Activity className="h-4 w-4 text-purple-400" />
          <div className="mt-2 font-medium text-sm">Financeiro</div>
          <div className="text-[10px] text-muted-foreground">DRE + ledger</div>
        </Link>
        <Link to="/operacoes" className="block p-3 rounded-lg border border-border bg-card hover:bg-muted/30 transition">
          <AlertCircle className="h-4 w-4 text-rose-400" />
          <div className="mt-2 font-medium text-sm">Operações</div>
          <div className="text-[10px] text-muted-foreground">Anomalies + health</div>
        </Link>
      </div>
    </div>
  );
}
